import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useAxios from '../../api/axios';
import { useAuth } from '../../context/AuthProvider';

const formatBytes = (value) => {
  const size = Number(value);

  if (!Number.isFinite(size) || size < 1024) {
    return `${Number.isFinite(size) ? size : 0} B`;
  }

  const units = ['KB', 'MB', 'GB'];
  let formatted = size / 1024;
  let unitIndex = 0;

  while (formatted >= 1024 && unitIndex < units.length - 1) {
    formatted /= 1024;
    unitIndex += 1;
  }

  return `${new Intl.NumberFormat('fi-FI', {
    minimumFractionDigits: formatted >= 10 ? 0 : 1,
    maximumFractionDigits: 1,
  }).format(formatted)} ${units[unitIndex]}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('fi-FI', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(parsed);
};

const AuditLogs = () => {
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const api = useAxios();
  const apiRef = useRef(api);
  const selectedFileNameRef = useRef('');
  const { auth, isAdmin } = useAuth();

  apiRef.current = api;
  selectedFileNameRef.current = selectedFileName;

  const userIsAdmin = Boolean(isAdmin?.() ?? auth?.isAdmin ?? auth?.user?.isAdmin);
  const accessToken = auth?.accessToken ?? '';

  const loadFileContent = useCallback(async (fileName) => {
    if (!userIsAdmin || !fileName) {
      return;
    }

    try {
      setLoadingContent(true);

      const response = await apiRef.current.get('/admin/getAuditLog', {
        params: { file: fileName },
        headers: accessToken ? { 'x-auth-token': accessToken } : {},
        withCredentials: true,
      });

      setSelectedFile(response?.data ?? null);
      setSelectedFileName(fileName);
    } catch (err) {
      console.error('getAuditLog failed', err);
      setSelectedFile(null);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Lokitiedoston avaus epäonnistui';
      toast.error(errorMessage);
    } finally {
      setLoadingContent(false);
    }
  }, [accessToken, userIsAdmin]);

  const loadFiles = useCallback(async () => {
    if (!userIsAdmin) {
      setLoadingFiles(false);
      return;
    }

    try {
      setLoadingFiles(true);

      const response = await apiRef.current.get('/admin/getAuditLogs', {
        headers: accessToken ? { 'x-auth-token': accessToken } : {},
        withCredentials: true,
      });

      const nextFiles = Array.isArray(response?.data?.files) ? response.data.files : [];
      setFiles(nextFiles);

      if (!nextFiles.length) {
        setSelectedFileName('');
        setSelectedFile(null);
        return;
      }

      const nextSelectedFile = nextFiles.find((item) => item.name === selectedFileNameRef.current) ?? nextFiles[0];
      loadFileContent(nextSelectedFile.name);
    } catch (err) {
      console.error('getAuditLogs failed', err);
      setFiles([]);
      setSelectedFile(null);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Lokitiedostojen haku epäonnistui';
      toast.error(errorMessage);
    } finally {
      setLoadingFiles(false);
    }
  }, [accessToken, loadFileContent, userIsAdmin]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  if (!userIsAdmin) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-center text-slate-700 shadow-sm">
          Tämä sivu on tarkoitettu vain ylläpidolle.
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 text-slate-900">
      <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-blue-700">Hallinta</p>
            <h1 className="mt-2 text-3xl font-black text-slate-900">Audit lokit</h1>
            <p className="mt-2 text-sm text-slate-600">
              Näkymä palvelimen lokitiedostoihin. Valitse tiedosto listalta avataksesi sen.
            </p>
          </div>

          <button
            type="button"
            onClick={loadFiles}
            disabled={loadingFiles || loadingContent}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-400 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingFiles ? 'Päivitetään...' : 'Päivitä lista'}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4">
            <h2 className="text-lg font-bold text-slate-900">Lokitiedostot</h2>
            <p className="mt-1 text-sm text-slate-500">Kaikki palvelimen log-hakemistosta löytyvät tiedostot.</p>
          </div>

          {loadingFiles ? (
            <div className="p-5 text-sm text-slate-600">Ladataan lokitiedostoja...</div>
          ) : files.length === 0 ? (
            <div className="p-5 text-sm text-slate-600">Lokitiedostoja ei löytynyt.</div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto p-3">
              <div className="space-y-2">
                {files.map((file) => {
                  const active = selectedFileName === file.name;

                  return (
                    <button
                      key={file.name}
                      type="button"
                      onClick={() => loadFileContent(file.name)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left transition ${active ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50'}`}
                    >
                      <div className="break-all text-sm font-semibold text-slate-900">{file.name}</div>
                      <div className="mt-2 text-xs text-slate-500">Muokattu: {formatDateTime(file.modifiedAt)}</div>
                      <div className="mt-1 text-xs text-slate-500">Koko: {formatBytes(file.size)}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Sisältö</h2>
                <p className="mt-1 text-sm text-slate-500">{selectedFile?.name ?? 'Valitse lokitiedosto listalta.'}</p>
              </div>
              {selectedFile ? (
                <div className="text-sm text-slate-500">
                  {formatBytes(selectedFile.size)} · {formatDateTime(selectedFile.modifiedAt)}
                </div>
              ) : null}
            </div>
          </div>

          {loadingContent ? (
            <div className="p-5 text-sm text-slate-600">Avataan lokitiedostoa...</div>
          ) : !selectedFile ? (
            <div className="p-5 text-sm text-slate-600">Valitse lokitiedosto nähdäksesi sen sisällön.</div>
          ) : (
            <pre className="max-h-[70vh] overflow-auto bg-slate-950 px-5 py-4 text-xs leading-6 text-slate-100">{selectedFile.content}</pre>
          )}
        </section>
      </div>
    </div>
  );
};

export default AuditLogs;